import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faArrowRight,
  faBuilding,
  faCheck,
  faChevronDown,
  faCopy,
  faCrown,
  faLayerGroup,
  faLock,
  faPlus,
  faTimes,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import { Country, State } from "country-state-city";
import logo from "../assets/logo.png";
import { supabase } from "../supabaseClient";
import { withRequestedStripeMode } from "../stripeMode";
import { useAuth } from "../AuthContext";
import {
  calculateInstitutionMonthlyPrice,
  calculateInstitutionOverageBreakdown,
  formatInstitutionEffectiveRate,
  formatInstitutionPrice,
  institutionPriceRequiresContact,
} from "../services/institutionPricing";
import { loadInstitutionBillingRows, mergeInstitutionPlansWithBillingRows } from "../services/institutionBillingPlans";
import { loadInstitutionBusinessDemoConfig } from "../services/institutionDemoMode";

const BRAND = {
  bg: "radial-gradient(circle at 88% 2%,rgba(12,105,200,0.12),transparent 30rem),linear-gradient(180deg,#F9FCFF 0%,#EEF4FA 58%,#F7FAFD 100%)",
  bgBand: "#F4F8FC",
  card: "#FFFFFF",
  elevated: "#F6F9FD",
  border: "#DCE6F1",
  borderStrong: "#C7D7E8",
  text: "#10233F",
  muted: "#526581",
  faint: "#718198",
  accent: "#0C69C8",
  accent2: "#1675D1",
  warn: "#F59E0B",
  danger: "#EF4444",
  grad: "linear-gradient(135deg,#0C69C8 0%,#0A1D44 100%)",
  greenGrad: "linear-gradient(135deg,#2189EA 0%,#0C69C8 55%,#0A1D44 100%)",
  shadow: "0 18px 54px rgba(25,52,92,0.12)",
};

const CLUB_ELITE_METADATA = {
  usage_based: true,
  included_members: 300,
  overage_bands: [{ from: 301, to: null, amount_cents: 125 }],
};
const ENTERPRISE_METADATA = {
  usage_based: true,
  requires_contact: true,
  public_price_label: "Custom pricing",
  estimate_base_amount_cents: 199900,
  included_members: 300,
  overage_bands: [
    { from: 301, to: 1000, amount_cents: 300 },
    { from: 1001, to: 5000, amount_cents: 200 },
    { from: 5001, to: 20000, amount_cents: 100 },
    { from: 20001, to: null, amount_cents: 50 },
  ],
};

const PLANS = [
  {
    segment: "Social",
    tier: "friends",
    name: "Friends",
    size: "Up to 5 people",
    monthly: "€4.99",
    yearly: "€50.90",
    baseAmountCents: 499,
    monthlyProductKey: "institutions_friends_monthly",
    yearlyProductKey: "institutions_friends_yearly",
    memberLimit: 5,
    typeHint: "Collective Group",
    subgroupLabel: "none",
    perMember: "~€1/member/month at 5 members",
    items: ["Step leaderboard", "App dashboard", "No team institutes"],
  },
  {
    segment: "Social",
    tier: "crew",
    name: "Crew",
    size: "Up to 15 people",
    monthly: "€14.99",
    yearly: "€152.90",
    baseAmountCents: 1499,
    monthlyProductKey: "institutions_crew_monthly",
    yearlyProductKey: "institutions_crew_yearly",
    memberLimit: 15,
    typeHint: "Collective Group",
    subgroupLabel: "none",
    perMember: "~€1/member/month at 15 members",
    items: ["Step leaderboard", "App dashboard", "No team institutes"],
  },
  {
    segment: "Social",
    tier: "club",
    name: "Club",
    size: "Up to 30 people",
    monthly: "€29",
    yearly: "€295.80",
    baseAmountCents: 2900,
    monthlyProductKey: "institutions_club_monthly",
    yearlyProductKey: "institutions_club_yearly",
    memberLimit: 30,
    typeHint: "Club",
    subgroupLabel: "none",
    perMember: "~€0.97/member/month at 30 members",
    items: ["Step leaderboard", "App dashboard", "No team institutes"],
  },
  {
    segment: "Club",
    tier: "club_starter",
    name: "Club Starter",
    size: "Up to 50 members",
    monthly: "€49",
    yearly: "€499.80",
    baseAmountCents: 4900,
    yearlyAmountCents: 49980,
    monthlyProductKey: "institutions_club_starter_monthly",
    yearlyProductKey: "institutions_club_starter_yearly",
    memberLimit: 50,
    typeHint: "Club",
    subgroupLabel: "teams",
    perMember: "€0.98/member/month at 50 members",
    items: ["Step leaderboard", "App dashboard", "Custom branding", "Team institutes", "Priority support", "Onboarding"],
  },
  {
    segment: "Club",
    tier: "club_plus",
    name: "Club Plus",
    size: "Up to 150 members",
    monthly: "€99",
    yearly: "€1,009.80",
    baseAmountCents: 9900,
    yearlyAmountCents: 100980,
    monthlyProductKey: "institutions_club_plus_monthly",
    yearlyProductKey: "institutions_club_plus_yearly",
    memberLimit: 150,
    typeHint: "Club",
    subgroupLabel: "teams",
    perMember: "€0.66/member/month at 150 members",
    items: ["Step leaderboard", "App dashboard", "Custom branding", "Team institutes", "Priority support", "Onboarding"],
  },
  {
    segment: "Club",
    tier: "club_pro",
    name: "Club Pro",
    size: "Up to 300 members",
    monthly: "€199",
    yearly: "€2,029.80",
    baseAmountCents: 19900,
    yearlyAmountCents: 202980,
    monthlyProductKey: "institutions_club_pro_monthly",
    yearlyProductKey: "institutions_club_pro_yearly",
    memberLimit: 300,
    typeHint: "Club",
    subgroupLabel: "teams",
    perMember: "~€0.66/member/month at 300 members",
    items: ["Step leaderboard", "App dashboard", "Custom branding", "Analytics and exporting", "Team institutes", "Priority support", "Onboarding", "Live dashboard"],
  },
  {
    segment: "Club",
    tier: "club_elite",
    name: "Club Elite",
    size: "301+ members",
    cardCta: "Estimate your price",
    monthly: "€449",
    yearly: "€4,579.80",
    baseAmountCents: 44900,
    yearlyAmountCents: 457980,
    metadata: CLUB_ELITE_METADATA,
    monthlyProductKey: "institutions_club_elite_monthly",
    yearlyProductKey: "institutions_club_elite_yearly",
    memberLimit: null,
    typeHint: "Club",
    subgroupLabel: "teams",
    perMember: "300 included, then €1.25/member/month",
    items: ["Step leaderboard", "App dashboard", "Custom branding", "Analytics and exporting", "Team institutes", "Priority support", "Onboarding", "Live dashboard"],
  },
  {
    segment: "Business",
    tier: "team",
    name: "Team",
    size: "Up to 20 people",
    monthly: "€149",
    yearly: "€1,519.80",
    baseAmountCents: 14900,
    monthlyProductKey: "institutions_team_monthly",
    yearlyProductKey: "institutions_team_yearly",
    memberLimit: 20,
    featured: true,
    typeHint: "Company",
    subgroupLabel: "departments",
    perMember: "€7.45/member/month at 20 members",
    items: ["Full web dashboard", "Custom branding", "Analytics and exporting", "Member and department institutes", "Priority support", "Onboarding", "Live dashboard"],
  },
  {
    segment: "Business",
    tier: "startup",
    name: "Startup",
    size: "Up to 50 people",
    monthly: "€299",
    yearly: "€3,049.80",
    baseAmountCents: 29900,
    monthlyProductKey: "institutions_startup_monthly",
    yearlyProductKey: "institutions_startup_yearly",
    memberLimit: 50,
    typeHint: "Company",
    subgroupLabel: "departments",
    perMember: "€5.98/member/month at 50 members",
    items: ["Full web dashboard", "Custom branding", "Analytics and exporting", "Member and department institutes", "Priority support", "Onboarding", "Live dashboard"],
  },
  {
    segment: "Business",
    tier: "growth",
    name: "Growth",
    size: "Up to 150 people",
    monthly: "€1,299",
    yearly: "€13,249.80",
    baseAmountCents: 129900,
    monthlyProductKey: "institutions_growth_monthly",
    yearlyProductKey: "institutions_growth_yearly",
    memberLimit: 150,
    typeHint: "Company",
    subgroupLabel: "departments",
    perMember: "€8.66/member/month at 150 members",
    items: ["Full web dashboard", "Custom branding", "Analytics and exporting", "Member and department institutes", "Priority support", "Onboarding", "Live dashboard"],
  },
  {
    segment: "Business",
    tier: "scale",
    name: "Scale",
    size: "Up to 300 people",
    monthly: "€1,999",
    yearly: "€20,389.80",
    baseAmountCents: 199900,
    monthlyProductKey: "institutions_scale_monthly",
    yearlyProductKey: "institutions_scale_yearly",
    memberLimit: 300,
    typeHint: "Company",
    subgroupLabel: "departments",
    perMember: "~€6.66/member/month at 300 members",
    items: ["Full web dashboard", "Custom branding", "Analytics and exporting", "Member and department institutes", "Priority support", "Onboarding", "Live dashboard"],
  },
  {
    segment: "Business",
    tier: "enterprise",
    name: "Enterprise",
    size: "Custom size",
    monthly: "Custom pricing",
    yearly: "Custom pricing",
    baseAmountCents: 199900,
    metadata: ENTERPRISE_METADATA,
    monthlyProductKey: "institutions_enterprise_monthly",
    yearlyProductKey: "institutions_enterprise_yearly",
    memberLimit: null,
    typeHint: "Company",
    subgroupLabel: "departments",
    perMember: "Starts at ~€6.66/member/month",
    cardCta: "Request rollout",
    items: ["Full web dashboard", "Custom branding", "Analytics and exporting", "Member and department institutes", "Priority support", "Onboarding", "Live dashboard"],
  },
];
const institutionPlanDisplayName = (plan) => plan?.segment === "Club" ? plan.name : [plan?.segment, plan?.name].filter(Boolean).join(" ");
const isBusinessInstitutionPlan = (plan) => String(plan?.segment || "").toLowerCase() === "business";
const createBusinessDemoDisplayPlan = (basePlan = {}) => ({
  ...basePlan,
  tier: `${basePlan.tier || "business"}_demo_card`,
  name: basePlan.name || "Enterprise",
  size: basePlan.size || "Custom size",
  featured: false,
  perMember: "",
  cardCta: basePlan.cardCta || "Book a Demo",
  items: [
    "Member and department institutes",
    "Live web dashboards",
    "Analytics and exporting",
    "Custom branding",
    "Onboarding support",
  ],
  isBusinessDemoCard: true,
  sourcePlan: basePlan,
});
const collapseBusinessDemoPlans = (plans = [], demoMode = false) => {
  if (!demoMode) return plans;
  const businessPlan = plans.find((plan) => String(plan?.tier || "").toLowerCase() === "enterprise") || plans.find(isBusinessInstitutionPlan);
  if (!businessPlan) return plans;
  return [
    ...plans.filter((plan) => !isBusinessInstitutionPlan(plan)),
    createBusinessDemoDisplayPlan(businessPlan),
  ];
};

const INSTITUTE_TYPES = ["Collective Group", "Club", "Gym", "Company", "Organisation", "School", "College", "Challenge"];
const COUNTRIES = Country.getAllCountries();
const CONTACT_EMAIL = "institutes@snowsproam.com";
const parseEuro = (value) => Number(String(value).replace(/[€,]/g, ""));
const toDateInputValue = (date) => {
  const next = new Date(date);
  if (!Number.isFinite(next.getTime())) return "";
  return next.toISOString().slice(0, 10);
};

const findCountry = (value) => (
  COUNTRIES.find((item) => item.isoCode === value || item.name.toLowerCase() === String(value || "").toLowerCase())
);

const planItemState = (item) => {
  const unavailable = /^no\s+/i.test(item);
  const addon = /add-on/i.test(item);
  return {
    unavailable,
    addon,
    label: unavailable ? item.replace(/^no\s+/i, "") : item,
  };
};

const planIncludesLiveDashboard = (plan) => (
  (plan?.items || []).some((item) => /live dashboard/i.test(item))
);

const field = (C, invalid = false) => ({
  width: "100%",
  minHeight: 46,
  borderRadius: 10,
  border: `1px solid ${invalid ? "rgba(239,68,68,0.72)" : C.borderStrong}`,
  background: C.card,
  color: C.text,
  padding: "0 13px",
  fontSize: 14,
  fontWeight: 700,
  outline: "none",
  boxSizing: "border-box",
});

const labelStyle = (C) => ({
  display: "grid",
  gap: 7,
  color: C.muted,
  fontSize: 12,
  fontWeight: 900,
  textTransform: "uppercase",
  letterSpacing: 0.4,
});

const ActionButton = ({ children, disabled, onClick, secondary = false, type = "button", C }) => (
  <button
    className={`institution-action-button ${secondary ? "is-secondary" : "is-primary"}`}
    type={type}
    disabled={disabled}
    onClick={onClick}
    style={{
      border: `1px solid ${secondary ? C.borderStrong : "transparent"}`,
      background: secondary ? C.elevated : C.grad,
      color: secondary ? C.text : "#FFFFFF",
      borderRadius: 11,
      minHeight: 46,
      padding: "0 16px",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      fontWeight: 900,
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.58 : 1,
      fontFamily: "'DM Sans',sans-serif",
    }}
  >
    {children}
  </button>
);

const PlanCard = ({ C, plan, active, cycle, onSelect, priceLabel, institutionBusinessDemoMode = false }) => {
  const planTone = plan.segment === "Business" ? C.accent2 : C.accent;
  const businessDemoPlan = institutionBusinessDemoMode && (plan.isBusinessDemoCard || isBusinessInstitutionPlan(plan));
  return (
    <button
      className={`institution-plan-card${active ? " active" : ""}`}
      type="button"
      onClick={onSelect}
      aria-pressed={active}
      style={{
        position: "relative",
        textAlign: "left",
        background: active ? "linear-gradient(180deg,#F4F9FF 0%,#EAF3FE 100%)" : C.card,
        border: `1.5px solid ${active ? C.accent : C.border}`,
        borderRadius: 14,
        padding: 16,
        color: C.text,
        boxShadow: active ? "0 0 0 3px rgba(12,105,200,0.13),0 16px 38px rgba(12,105,200,0.12)" : "0 10px 30px rgba(25,52,92,0.06)",
        cursor: "pointer",
        minHeight: 244,
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }}
    >
      {plan.featured && (
        <span style={{ position: "absolute", right: 12, top: 12, borderRadius: 999, padding: "5px 8px", background: C.greenGrad, color: "#fff", fontSize: 10, fontWeight: 950 }}>
          Best Start
        </span>
      )}
      <span style={{ color: planTone, fontSize: 11, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.5 }}>{plan.segment}</span>
      <div>
        <div style={{ fontSize: 21, fontWeight: 950, marginBottom: 2 }}>{institutionPlanDisplayName(plan)}</div>
        <div style={{ color: planTone, fontSize: 12, fontWeight: 900 }}>{plan.size}</div>
      </div>
      <div style={{ fontSize: 24, fontWeight: 950 }}>
        {priceLabel}
        {!businessDemoPlan && !plan.cardPriceLabel && !plan.metadata?.public_price_label && <span style={{ color: C.faint, fontSize: 12, fontWeight: 800 }}>/{cycle === "yearly" ? "year" : "month"}</span>}
      </div>
      {plan.perMember && !businessDemoPlan && (
        <div style={{ color: planTone, fontSize: 11, fontWeight: 900, marginTop: -6 }}>{plan.perMember}</div>
      )}
      <div style={{ display: "grid", gap: 7 }}>
        {plan.items.map((item) => {
          const state = planItemState(item);
          return (
          <span key={item} style={{ color: C.muted, fontSize: 12, fontWeight: 760, lineHeight: 1.35 }}>
            <FontAwesomeIcon
              icon={state.unavailable ? faTimes : state.addon ? faPlus : faCheck}
              style={{ color: state.unavailable ? C.danger : state.addon ? C.warn : C.accent2, marginRight: 7, fontSize: 12, width: 12 }}
            />
            <span>
              {state.label}
            </span>
          </span>
          );
        })}
      </div>
      <div style={{ marginTop: "auto", color: active ? C.accent2 : C.faint, fontSize: 12, fontWeight: 900 }}>
        {businessDemoPlan ? "Book a Demo" : active ? "Selected" : plan.cardCta || (plan.metadata?.requires_contact ? "Request rollout" : "Choose plan")}
      </div>
    </button>
  );
};

export default function InstitutionCreate() {
  const C = BRAND;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const liveDashboardPaywall = searchParams.get("feature") === "live-dashboard";
  const challengeSetup = searchParams.get("setupMode") === "challenge" || searchParams.get("type") === "challenge";
  const existingInstituteIdParam = searchParams.get("institute_id") || "";
  const existingWorkspaceIdParam = searchParams.get("workspace_id") || "";
  const restartExistingAccess = searchParams.get("restart") === "1" || searchParams.get("mode") === "restart";
  const requestedPlanTier = String(searchParams.get("plan_tier") || "").toLowerCase();
  const requestedBillingCycle = ["monthly", "yearly"].includes(String(searchParams.get("billing_cycle") || "").toLowerCase())
    ? String(searchParams.get("billing_cycle")).toLowerCase()
    : "monthly";
  const existingInstituteRequested = !!(existingInstituteIdParam || existingWorkspaceIdParam);
  const { user, profile } = useAuth();
  const challengeNameInputRef = useRef(null);
  const institutionTypeSelectRef = useRef(null);
  const [cycle, setCycle] = useState(requestedBillingCycle);
  const [segment, setSegment] = useState("all");
  const [selectedTier, setSelectedTier] = useState(requestedPlanTier || (liveDashboardPaywall ? "club_pro" : "team"));
  const [workspaceName, setWorkspaceName] = useState("");
  const [name, setName] = useState("");
  const [type, setType] = useState(challengeSetup ? "Challenge" : "Company");
  const initialCountry = findCountry(profile?.country || profile?.current_country || "Ireland") || findCountry("Ireland") || COUNTRIES[0];
  const [countryCode, setCountryCode] = useState(initialCountry?.isoCode || "");
  const [country, setCountry] = useState(initialCountry?.name || "Ireland");
  const [region, setRegion] = useState(profile?.region || profile?.state_province || "");
  const [contactName, setContactName] = useState(profile?.name || "");
  const [contactEmailAddress, setContactEmailAddress] = useState(profile?.email || user?.email || "");
  const [contactPhone, setContactPhone] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [primaryColor, setPrimaryColor] = useState("#0C69C8");
  const [accentColor, setAccentColor] = useState("#34D399");
  const [activeMemberCount, setActiveMemberCount] = useState(300);
  const [challengeStartsOn, setChallengeStartsOn] = useState(() => challengeSetup ? toDateInputValue(new Date()) : "");
  const [challengeEndsOn, setChallengeEndsOn] = useState(() => {
    if (!challengeSetup) return "";
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return toDateInputValue(tomorrow);
  });
  const [challengeMemberLimit, setChallengeMemberLimit] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [created, setCreated] = useState(null);
  const [demoSubmitted, setDemoSubmitted] = useState("");
  const [institutionBusinessDemoMode, setInstitutionBusinessDemoMode] = useState(false);
  const [institutionBusinessDemoCalendlyUrl, setInstitutionBusinessDemoCalendlyUrl] = useState("");
  const [institutionPublicPricingHidden, setInstitutionPublicPricingHidden] = useState(true);
  const [billingRows, setBillingRows] = useState([]);
  const [billingPricesStatus, setBillingPricesStatus] = useState("loading");
  const [existingInstitute, setExistingInstitute] = useState(null);
  const [existingInstituteStatus, setExistingInstituteStatus] = useState(existingInstituteRequested ? "loading" : "idle");
  const [existingInstituteError, setExistingInstituteError] = useState("");

  useEffect(() => {
    if (!existingInstituteRequested) {
      setExistingInstitute(null);
      setExistingInstituteStatus("idle");
      setExistingInstituteError("");
      return;
    }

    if (!user?.id) {
      setExistingInstitute(null);
      setExistingInstituteStatus("idle");
      setExistingInstituteError("Sign in before restarting institution access.");
      return;
    }

    let mounted = true;
    const loadExistingInstitute = async () => {
      setExistingInstituteStatus("loading");
      setExistingInstituteError("");
      try {
        let query = supabase
          .from("institutes")
          .select("id,name,institute_type,country,state_province,image_url,brand_logo_url,brand_settings,workspace_id,plan_tier,billing_status,teams,archived_at")
          .is("archived_at", null);

        if (existingInstituteIdParam) {
          query = query.eq("id", existingInstituteIdParam).maybeSingle();
        } else {
          query = query.eq("workspace_id", existingWorkspaceIdParam).neq("institute_type", "Challenge").limit(1).maybeSingle();
        }

        const { data: row, error } = await query;
        if (error) throw error;
        if (!row?.id) throw new Error("Could not find that institution for this account.");

        const { data: accessRow } = await supabase
          .from("institutes_with_steps")
          .select("id,plan_tier,billing_status,access_active")
          .eq("id", row.id)
          .maybeSingle();

        if (!mounted) return;
        const next = {
          ...row,
          plan_tier: row.plan_tier || accessRow?.plan_tier || row.brand_settings?.planTier || null,
          billing_status: accessRow?.billing_status ?? row.billing_status ?? null,
          access_active: accessRow?.access_active,
        };
        setExistingInstitute(next);

        const nextCountry = findCountry(row.country || profile?.country || profile?.current_country || "Ireland");
        setWorkspaceName(row.name || "");
        setName(row.name || "");
        setType(row.institute_type || "Company");
        setCountryCode(nextCountry?.isoCode || "");
        setCountry(nextCountry?.name || row.country || "");
        setRegion(row.state_province || "");
        setLogoUrl(row.brand_logo_url || row.image_url || "");
        if (row.brand_settings?.primaryColor) setPrimaryColor(row.brand_settings.primaryColor);
        if (row.brand_settings?.accentColor) setAccentColor(row.brand_settings.accentColor);
        setExistingInstituteStatus("ready");
      } catch (error) {
        if (!mounted) return;
        setExistingInstitute(null);
        setExistingInstituteStatus("error");
        setExistingInstituteError(error?.message || "Could not load this institution.");
      }
    };

    loadExistingInstitute();
    return () => {
      mounted = false;
    };
  }, [existingInstituteIdParam, existingInstituteRequested, existingWorkspaceIdParam, profile?.country, profile?.current_country, user?.id]);

  useEffect(() => {
    let mounted = true;
    const loadBillingPrices = async () => {
      setBillingPricesStatus("loading");
      try {
        const rows = await loadInstitutionBillingRows();
        if (!mounted) return;
        setBillingRows(rows);
        setBillingPricesStatus("ready");
      } catch {
        if (!mounted) return;
        setBillingPricesStatus("error");
      }
    };

    loadBillingPrices();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    loadInstitutionBusinessDemoConfig()
      .then((config) => {
        if (mounted) {
          setInstitutionBusinessDemoMode(config.demoMode);
          setInstitutionBusinessDemoCalendlyUrl(config.calendlyUrl);
          setInstitutionPublicPricingHidden(config.publicPricingHidden);
        }
      })
      .catch(() => {
        if (mounted) {
          setInstitutionBusinessDemoMode(false);
          setInstitutionBusinessDemoCalendlyUrl("");
          setInstitutionPublicPricingHidden(true);
        }
      });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!contactName && profile?.name) setContactName(profile.name);
    if (!contactEmailAddress && (profile?.email || user?.email)) setContactEmailAddress(profile?.email || user?.email || "");
  }, [contactEmailAddress, contactName, profile?.email, profile?.name, user?.email]);

  useEffect(() => {
    if (!challengeSetup) return;
    setType("Challenge");
    setName((current) => current || "");
    setChallengeStartsOn((current) => current || toDateInputValue(new Date()));
    setChallengeEndsOn((current) => {
      if (current) return current;
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      return toDateInputValue(tomorrow);
    });
  }, [challengeSetup]);

  const plans = useMemo(() => mergeInstitutionPlansWithBillingRows(PLANS, billingRows), [billingRows]);
  const existingCheckoutMode = existingInstituteRequested || !!existingInstitute?.id;
  const existingStoredTier = String(
    requestedPlanTier ||
    existingInstitute?.plan_tier ||
    existingInstitute?.brand_settings?.planTier ||
    ""
  ).toLowerCase();
  const lockedExistingPlan = restartExistingAccess && existingStoredTier
    ? plans.find((plan) => plan.tier === existingStoredTier) || null
    : null;
  const restartPlanBlocked = restartExistingAccess && (!existingStoredTier || !lockedExistingPlan);
  const planPool = useMemo(() => {
    if (lockedExistingPlan) return [lockedExistingPlan];
    if (restartExistingAccess) return [];
    if (liveDashboardPaywall) return plans.filter(planIncludesLiveDashboard);
    return plans;
  }, [liveDashboardPaywall, lockedExistingPlan, plans, restartExistingAccess]);
  const visiblePlans = useMemo(() => (
    segment === "all" ? planPool : planPool.filter((plan) => plan.segment.toLowerCase() === segment)
  ), [planPool, segment]);
  const segmentButtons = useMemo(() => {
    const values = Array.from(new Set(planPool.map((plan) => plan.segment.toLowerCase()))).filter(Boolean);
    return values.length > 1 ? ["all", ...values] : values;
  }, [planPool]);
  const selectedPlan = lockedExistingPlan || visiblePlans.find((plan) => plan.tier === selectedTier) || visiblePlans[0] || planPool[0] || plans.find((plan) => plan.tier === "team") || plans[0];
  const displayPlans = useMemo(
    () => collapseBusinessDemoPlans(visiblePlans, institutionBusinessDemoMode),
    [institutionBusinessDemoMode, visiblePlans],
  );
  const selectedPlanTone = selectedPlan.segment === "Business" ? C.accent2 : C.accent;
  const checkoutPriceKey = cycle === "yearly" ? selectedPlan.yearlyProductKey : selectedPlan.monthlyProductKey;
  const productKey = checkoutPriceKey.replace(/_(monthly|yearly)$/, "");
  const getPlanFallbackAmountCents = (plan, priceCycle = cycle) => {
    if (priceCycle === "yearly") {
      const parsedYearly = parseEuro(plan.yearly);
      return Number.isFinite(parsedYearly) ? Math.round(parsedYearly * 100) : plan.baseAmountCents * 12;
    }
    return plan.baseAmountCents;
  };
  const getPlanAmountCents = (plan, priceCycle = cycle) => {
    const amount = priceCycle === "yearly" ? plan.yearlyAmountCents : plan.monthlyAmountCents;
    return amount ?? getPlanFallbackAmountCents(plan, priceCycle);
  };
  const getPlanPriceLabel = (plan) => {
    if (plan.cardPriceLabel) return plan.cardPriceLabel;
    if (plan.metadata?.public_price_label) return plan.metadata.public_price_label;
    const amountCents = getPlanAmountCents(plan);
    const prefix = plan.metadata?.usage_based ? "From " : "";
    return `${prefix}${formatInstitutionPrice(amountCents)}`;
  };
  const subgroupsIncluded = selectedPlan.segment === "Business" || selectedPlan.segment === "Club";
  const subgroupLabel = selectedPlan.subgroupLabel || (selectedPlan.segment === "Club" ? "teams" : selectedPlan.segment === "Business" ? "departments" : "none");
  const structureCopy = selectedPlan.segment === "Business" ? "Member and department institutes" : selectedPlan.segment === "Club" ? "Team institutes" : "No team institutes";
  const workspaceNameForCopy = workspaceName.trim();
  const generatedDashboardCopy = subgroupsIncluded
    ? workspaceNameForCopy
      ? `We will create ${workspaceNameForCopy} Members and ${structureCopy}.`
      : `We will create Members and ${structureCopy} from your workspace name.`
    : workspaceNameForCopy
      ? `We will create ${workspaceNameForCopy} Members.`
      : "We will create the Members dashboard from your workspace name.";
  const usageMetadata = selectedPlan.metadata || null;
  const usageBased = !!usageMetadata?.usage_based;
  const includedMembers = Number(usageMetadata?.included_members || 0);
  const effectiveMemberCount = Math.max(0, Math.floor(Number(activeMemberCount) || 0));
  const estimateBaseAmountCents = usageMetadata?.estimate_base_amount_cents || getPlanAmountCents(selectedPlan, "monthly");
  const yearlyBaseAmountCents = getPlanAmountCents(selectedPlan, "yearly");
  const monthlyOverageCents = usageBased
    ? calculateInstitutionOverageBreakdown(usageMetadata, effectiveMemberCount).reduce((sum, line) => sum + line.line_total_cents, 0)
    : 0;
  const estimatedMonthlyCents = usageBased
    ? calculateInstitutionMonthlyPrice(usageMetadata, effectiveMemberCount, estimateBaseAmountCents)
    : getPlanAmountCents(selectedPlan, "monthly");
  const estimatedYearlyCents = usageBased
    ? yearlyBaseAmountCents + monthlyOverageCents * 12
    : yearlyBaseAmountCents;
  const overageBreakdown = usageBased
    ? calculateInstitutionOverageBreakdown(usageMetadata, effectiveMemberCount)
    : [];
  const usageBelowIncluded = usageBased && includedMembers > 0 && effectiveMemberCount < includedMembers;
  const calculatedRolloutRequired = usageBased && includedMembers > 0 && effectiveMemberCount > includedMembers;
  const contactRequired = calculatedRolloutRequired || (usageBased
    ? institutionPriceRequiresContact(usageMetadata, effectiveMemberCount)
    : !!usageMetadata?.requires_contact);
  const businessDemoRequired = institutionBusinessDemoMode && isBusinessInstitutionPlan(selectedPlan);
  const calendlyDemoRequired = businessDemoRequired && !!institutionBusinessDemoCalendlyUrl;
  const businessDemoOnly = businessDemoRequired && !existingCheckoutMode && type !== "Challenge";
  const contactDetailsRequired = !businessDemoRequired && !usageBelowIncluded && (contactRequired || !!selectedPlan.cardCta || !!selectedPlan.metadata?.public_price_label);
  const customClubPricingRequired = selectedPlan.tier === "club_elite" && contactRequired;
  const regionOptions = useMemo(() => State.getStatesOfCountry(countryCode), [countryCode]);
  const selectedTotal = businessDemoRequired
    ? getPlanPriceLabel(selectedPlan)
    : usageBased
    ? customClubPricingRequired ? "Contact us" : formatInstitutionPrice(cycle === "yearly" ? estimatedYearlyCents : estimatedMonthlyCents)
    : formatInstitutionPrice(getPlanAmountCents(selectedPlan));
  const selectedTotalSuffix = businessDemoRequired ? "" : customClubPricingRequired ? "" : usageBased ? cycle === "yearly" ? "/year est." : "/month est." : cycle === "yearly" ? "/year" : "/month";
  const selectedBaseLabel = businessDemoRequired
    ? "Demo-led rollout"
    : usageBased
    ? `${formatInstitutionPrice(estimateBaseAmountCents)}/month estimate base`
    : `${formatInstitutionPrice(getPlanAmountCents(selectedPlan))}${selectedTotalSuffix}`;
  const perMemberDivisor = usageBased ? Math.max(1, effectiveMemberCount) : Math.max(1, Number(selectedPlan.memberLimit || selectedPlan.limit || 1));
  const perMemberMonthlyTotalCents = usageBased ? estimatedMonthlyCents : getPlanAmountCents(selectedPlan, "monthly");
  const perMemberYearlyTotalCents = usageBased ? estimatedYearlyCents : getPlanAmountCents(selectedPlan, "yearly");

  const choosePlan = (plan) => {
    if (lockedExistingPlan) return;
    setSelectedTier(plan.tier);
    if (!existingCheckoutMode) {
      setType((current) => (challengeSetup || current === "Challenge" ? "Challenge" : plan.typeHint));
    }
    setErrors((prev) => ({ ...prev, plan: null }));
  };
  const chooseSegment = (value) => {
    if (lockedExistingPlan) return;
    setSegment(value);
    const nextPlans = value === "all" ? planPool : planPool.filter((plan) => plan.segment.toLowerCase() === value);
    const currentVisible = nextPlans.some((plan) => plan.tier === selectedTier);
    if (!currentVisible && nextPlans[0]) choosePlan(nextPlans[0]);
  };

  const chooseCountry = (nextCode) => {
    const nextCountry = findCountry(nextCode);
    setCountryCode(nextCode);
    setCountry(nextCountry?.name || "");
    setRegion("");
  };

  const handleWorkspaceNameKeyDown = (event) => {
    if (event.key !== "Enter" || event.nativeEvent?.isComposing) return;
    event.preventDefault();
    if (!existingCheckoutMode && type === "Challenge") {
      challengeNameInputRef.current?.focus();
      return;
    }
    institutionTypeSelectRef.current?.focus();
  };

  const validate = () => {
    const next = {};
    if (restartPlanBlocked) next.submit = "The previous membership plan could not be confirmed, so restart checkout is blocked.";
    if (existingCheckoutMode && existingInstituteStatus === "loading") next.submit = "Institution details are still loading.";
    if (existingCheckoutMode && !existingInstitute?.id) next.submit = existingInstituteError || "Could not load this institution.";
    if (!businessDemoOnly && !existingCheckoutMode && !workspaceName.trim()) next.workspaceName = "Enter the account or workspace name.";
    if (!existingCheckoutMode && type === "Challenge" && !name.trim()) next.name = "Enter a challenge name.";
    if (!businessDemoOnly && !existingCheckoutMode && !type.trim()) next.type = "Choose an institution type.";
    if (!businessDemoOnly && !existingCheckoutMode && !country.trim()) next.country = "Enter a country.";
    if (!businessDemoOnly && !existingCheckoutMode && !region.trim()) next.region = "Choose or enter a region.";
    if (contactDetailsRequired) {
      if (!contactName.trim()) next.contactName = "Enter a contact name.";
      if (!contactEmailAddress.trim()) next.contactEmailAddress = "Enter a contact email.";
    }
    if (type === "Challenge") {
      const start = new Date(`${challengeStartsOn}T00:00:00`);
      const end = new Date(`${challengeEndsOn}T23:59:59`);
      const today = new Date(`${toDateInputValue(new Date())}T00:00:00`);
      const limit = Math.floor(Number(challengeMemberLimit));
      if (!challengeStartsOn || !Number.isFinite(start.getTime())) next.challengeStartsOn = "Choose a start date.";
      if (!challengeEndsOn || !Number.isFinite(end.getTime())) next.challengeEndsOn = "Choose a finish date.";
      if (challengeStartsOn && Number.isFinite(start.getTime()) && start < today) next.challengeStartsOn = "Start date must be today or later.";
      if (challengeStartsOn && challengeEndsOn && !(end > start)) next.challengeEndsOn = "Finish date must be after the start date.";
      if (!Number.isFinite(limit) || limit <= 0) next.challengeMemberLimit = "Enter how many people can join.";
    }
    if (!user && !contactDetailsRequired) next.auth = "Sign in before creating an institution.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submitDemoRequest = async () => {
    if (!validate()) return;
    setSubmitting(true);
    setErrors({});
    setDemoSubmitted("");
    try {
      const { data, error } = await supabase.functions.invoke("submit_demo_request", {
        body: {
          service: "institutions",
          is_institution: true,
          segment: selectedPlan.segment || "Business",
          plan_tier: selectedPlan.tier,
          plan_name: institutionPlanDisplayName(selectedPlan),
          name: contactName.trim(),
          email: contactEmailAddress.trim(),
          phone: contactPhone.trim() || null,
          organisation: workspaceName.trim() || name.trim() || existingInstitute?.name || "Institution",
          institution_type: existingInstitute?.institute_type || type || selectedPlan.typeHint || "Company",
          expected_members: effectiveMemberCount || null,
          message: [
            `Billing cycle selected: ${cycle}`,
            `Country: ${country || "Not set"}`,
            `Region: ${region || "Not set"}`,
            `Structure: ${structureCopy}`,
            existingCheckoutMode && existingInstitute?.id ? `Existing institute ID: ${existingInstitute.id}` : "",
          ].filter(Boolean).join("\n"),
          source_path: `${window.location.pathname}${window.location.search}${window.location.hash}`,
          metadata: {
            billing_cycle: cycle,
            country,
            region,
            member_count: effectiveMemberCount,
            included_members: includedMembers || null,
            existing_institute_id: existingInstitute?.id || null,
          },
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      const message = data?.email_sent
        ? "Demo request sent. We will contact you within 24 hours Monday to Friday."
        : `Demo request saved. Email delivery needs checking, but ${CONTACT_EMAIL} can follow up from the saved request.`;
      setDemoSubmitted(message);
      toast.success("Demo request sent");
    } catch (error) {
      setErrors({ submit: error?.message || "Could not send the demo request." });
    } finally {
      setSubmitting(false);
    }
  };

  const startTrial = async (event) => {
    event.preventDefault();
    if (calendlyDemoRequired) {
      window.open(institutionBusinessDemoCalendlyUrl, "_blank", "noopener,noreferrer");
      return;
    }
    if (usageBelowIncluded) {
      setErrors({
        submit: `${institutionPlanDisplayName(selectedPlan)} starts at ${includedMembers.toLocaleString()} active members. Choose a lower self-serve plan for fewer members.`,
      });
      return;
    }
    if (contactDetailsRequired) {
      await submitDemoRequest();
      return;
    }
    if (!validate()) return;

    const { data: sessionData } = await supabase.auth.getSession();
    const session = sessionData?.session;
    if (!session) {
      setErrors({ auth: existingCheckoutMode ? "Sign in before restarting institution access." : "Sign in before creating an institution." });
      return;
    }

    if (existingCheckoutMode) {
      if (!checkoutPriceKey) {
        setErrors({ submit: "This plan is not available for checkout." });
        return;
      }
      if (restartExistingAccess && lockedExistingPlan && selectedPlan.tier !== lockedExistingPlan.tier) {
        setErrors({ submit: "Restart is locked to the previous membership plan." });
        return;
      }

      setSubmitting(true);
      setErrors({});
      try {
        const { data, error } = await supabase.functions.invoke("institutions_create_checkout", {
          headers: { Authorization: `Bearer ${session.access_token}` },
          body: withRequestedStripeMode({
            price_key: checkoutPriceKey,
            institute_id: existingInstitute.id,
            workspace_name: existingInstitute.name || workspaceName.trim() || undefined,
            create_new_workspace: false,
            institute_name: existingInstitute.name || name.trim() || workspaceName.trim() || undefined,
            institute_type: existingInstitute.institute_type || type || selectedPlan.typeHint || "Company",
            country: existingInstitute.country || country.trim() || undefined,
            region: existingInstitute.state_province || region.trim() || undefined,
            success_url: `${window.location.origin}/profile?tab=institutes&checkout=institutions-success`,
            cancel_url: `${window.location.origin}/profile?tab=institutes&checkout=institutions-cancelled`,
            brand_settings: {
              ...(existingInstitute.brand_settings || {}),
              primaryColor,
              accentColor,
              planTier: selectedPlan.tier,
              billingCycle: cycle,
              segment: selectedPlan.segment.toLowerCase(),
              restartedExistingInstitution: restartExistingAccess,
              upgradedExistingInstitution: !restartExistingAccess,
            },
          }),
        });
        if (error) throw error;
        if (data?.error) throw new Error(data.error);
        if (!data?.url) throw new Error("Checkout URL was not returned.");
        window.location.href = data.url;
      } catch (err) {
        setErrors({ submit: err?.message || "Could not open checkout." });
      } finally {
        setSubmitting(false);
      }
      return;
    }

    setSubmitting(true);
    setErrors({});
    try {
      const { data, error } = await supabase.functions.invoke("institutions_start_trial", {
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: {
          product_key: productKey,
          workspace_name: workspaceName.trim(),
          create_new_workspace: true,
          institute_name: name.trim() || workspaceName.trim(),
          institute_type: type,
          country: country.trim(),
          region: region.trim() || null,
          logo_url: logoUrl.trim() || null,
          starts_at: type === "Challenge" ? new Date(`${challengeStartsOn}T00:00:00`).toISOString() : null,
          ends_at: type === "Challenge" ? new Date(`${challengeEndsOn}T23:59:59`).toISOString() : null,
          challenge_member_limit: type === "Challenge" ? Math.floor(Number(challengeMemberLimit)) : null,
          brand_settings: {
            primaryColor,
            accentColor,
            planTier: selectedPlan.tier,
            billingCycle: cycle,
            segment: selectedPlan.segment.toLowerCase(),
            subgroups: type === "Challenge" ? false : subgroupsIncluded,
            subgroupLabel: type === "Challenge" ? "members" : subgroupLabel,
            activeMemberCount: usageBased ? effectiveMemberCount : null,
            estimatedMonthlyCents: usageBased ? estimatedMonthlyCents : null,
            ...(type === "Challenge" ? {
              challengeMode: "members",
              challengeModeLabel: "Members",
              startsOn: new Date(`${challengeStartsOn}T00:00:00`).toISOString(),
              endsOn: new Date(`${challengeEndsOn}T23:59:59`).toISOString(),
            } : {}),
          },
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setCreated(data);
      toast.success("Institution access created");
    } catch (err) {
      setErrors({ submit: err?.message || "Could not create institution access." });
    } finally {
      setSubmitting(false);
    }
  };

  const copyCode = async () => {
    const code = created?.join_code || created?.institute_code;
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      toast.success("Join code copied");
    } catch {
      toast.info(code);
    }
  };

  if (institutionPublicPricingHidden) {
    return (
      <main className="institution-create-page" style={{ minHeight: '100vh', background: C.bg, color: C.text, fontFamily: "'DM Sans',sans-serif", display: 'grid', placeItems: 'center', padding: 20 }}>
        <section style={{ width: '100%', maxWidth: 620, background: C.card, border: `1px solid ${C.border}`, borderRadius: 20, padding: 'clamp(22px,5vw,38px)', boxShadow: C.shadow }}>
          <img src={logo} alt="Snows ProAm" style={{ height: 34, marginBottom: 24 }} />
          <h1 style={{ fontSize: 'clamp(27px,5vw,38px)', lineHeight: 1.12, margin: '0 0 12px' }}>Set up your institution with our team</h1>
          <p style={{ color: C.muted, lineHeight: 1.6, margin: '0 0 22px' }}>Tell us about your school, club or company and we’ll help you get started.</p>
          <Link to="/support" style={{ display: 'inline-flex', alignItems: 'center', minHeight: 44, padding: '10px 17px', borderRadius: 11, background: C.greenGrad, color: '#fff', fontWeight: 900, textDecoration: 'none' }}>Contact Snows ProAm</Link>
        </section>
      </main>
    );
  }

  return (
    <main className="institution-create-page" style={{ minHeight: "100vh", background: C.bg, color: C.text, fontFamily: "'DM Sans',sans-serif" }}>
      <header className="institution-create-header" style={{ minHeight: 68, padding: "0 22px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${C.border}`, background: "rgba(255,255,255,0.90)", position: "sticky", top: 0, zIndex: 20, backdropFilter: "blur(16px)" }}>
        <Link to="/" style={{ display: "inline-flex", alignItems: "center" }}>
          <img src={logo} alt="Snows ProAm" style={{ height: 30 }} />
        </Link>
        <button type="button" onClick={() => navigate(-1)} style={{ color: C.muted, fontSize: 13, fontWeight: 900, textDecoration: "none", display: "inline-flex", gap: 8, alignItems: "center", border: "none", background: "transparent", cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>
          <FontAwesomeIcon icon={faArrowLeft} />
          Back
        </button>
      </header>

      <section className="institution-create-content" style={{ maxWidth: 1320, margin: "0 auto", padding: "30px 22px 80px" }}>
        {restartExistingAccess ? (
          <section role="status" style={{ display: "flex", alignItems: "flex-start", gap: 11, marginBottom: 18, padding: 14, borderRadius: 12, border: `1px solid ${C.borderStrong}`, background: "rgba(52,211,153,0.12)" }}>
            <FontAwesomeIcon aria-hidden="true" icon={faLock} style={{ color: C.accent2, marginTop: 3 }} />
            <div>
              <strong style={{ display: "block", color: C.text }}>Restart membership</strong>
              <p style={{ margin: "5px 0 0", color: C.muted, fontSize: 13, lineHeight: 1.55 }}>
                This will restart access for the existing institution. Members, join codes, departments and step history stay in place.
              </p>
            </div>
          </section>
        ) : liveDashboardPaywall && (
          <section role="status" style={{ display: "flex", alignItems: "flex-start", gap: 11, marginBottom: 18, padding: 14, borderRadius: 12, border: `1px solid ${C.borderStrong}`, background: "rgba(12,105,200,0.14)" }}>
            <FontAwesomeIcon aria-hidden="true" icon={faLock} style={{ color: C.accent2, marginTop: 3 }} />
            <div>
              <strong style={{ display: "block", color: C.text }}>Live dashboard upgrade</strong>
              <p style={{ margin: "5px 0 0", color: C.muted, fontSize: 13, lineHeight: 1.55 }}>
                Your workspace needs an active live dashboard entitlement before it can publish or open a branded live page. Club Pro is selected as the first qualifying club plan. Club Elite and business plans also include access.
              </p>
            </div>
          </section>
        )}
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1.2fr) minmax(350px,0.8fr)", gap: 24, alignItems: "start" }} className="institution-create-grid">
          <div>
            <div className="institution-create-hero" style={{ marginBottom: 22, background: C.grad }}>
              <div className="institution-create-hero-orb one" aria-hidden="true" />
              <div className="institution-create-hero-orb two" aria-hidden="true" />
              <div style={{ position: "relative", zIndex: 1 }}>
              <div style={{ color: "#8FD3FF", fontSize: 12, fontWeight: 950, textTransform: "uppercase", letterSpacing: 1.1, marginBottom: 9 }}>Institutions / Step Leaderboards</div>
              <h1 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(32px,5vw,56px)", lineHeight: 1.03, margin: "0 0 12px", letterSpacing: -1.5, color: "#FFFFFF", maxWidth: 760 }}>
                {restartExistingAccess ? "Restart institution membership." : existingCheckoutMode ? "Update institution access." : type === "Challenge" ? "Create a step challenge." : "Create a step institution."}
              </h1>
              <p style={{ margin: 0, maxWidth: 760, color: "rgba(255,255,255,0.82)", fontSize: 16, lineHeight: 1.65, fontWeight: 650 }}>
                {restartExistingAccess
                  ? "Use the saved institution and plan. Nothing new is created."
                  : existingCheckoutMode
                    ? "Use the existing institution. Members, departments, branding and join codes stay the same."
                    : type === "Challenge"
                  ? "Create a time-limited step leaderboard, set the participant cap, then share the join code with members."
                  : "Start with a plan, create the institute, then invite members with an invite code. Team and above unlock departments and the full web dashboard."}
              </p>
              {!existingCheckoutMode && (
                <ol className="institution-create-steps" aria-label="Institution setup steps">
                  <li><span>1</span><strong>Choose a plan</strong></li>
                  <li><span>2</span><strong>Add your details</strong></li>
                  <li><span>3</span><strong>Invite members</strong></li>
                </ol>
              )}
              </div>
            </div>

            <div className="institution-plan-toolbar" style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
              {segmentButtons.length > 1 ? (
                <div style={{ display: "inline-flex", gap: 4, border: `1px solid ${C.border}`, background: C.card, padding: 4, borderRadius: 12 }}>
                  {segmentButtons.map((value) => (
                    <button key={value} type="button" onClick={() => chooseSegment(value)} style={{ border: "none", borderRadius: 9, background: segment === value ? C.grad : "transparent", color: segment === value ? "#fff" : C.muted, padding: "9px 12px", fontWeight: 900, textTransform: "capitalize", cursor: "pointer" }}>
                      {value}
                    </button>
                  ))}
                </div>
              ) : <span />}
              <div style={{ display: "inline-flex", gap: 4, border: `1px solid ${C.border}`, background: C.card, padding: 4, borderRadius: 12 }}>
                {[
                  ["monthly", "Monthly"],
                  ["yearly", "Yearly · save 15%"],
                ].map(([value, label]) => (
                  <button key={value} type="button" onClick={() => setCycle(value)} style={{ border: "none", borderRadius: 9, background: cycle === value ? C.greenGrad : "transparent", color: cycle === value ? "#fff" : C.muted, padding: "9px 12px", fontWeight: 900, cursor: "pointer" }}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {restartPlanBlocked ? (
              <div role="alert" style={{ border: `1px solid rgba(239,68,68,0.35)`, background: "rgba(239,68,68,0.08)", borderRadius: 12, padding: 14, color: C.danger, fontSize: 13, fontWeight: 850, lineHeight: 1.5 }}>
                Previous membership plan could not be confirmed. Restart checkout is blocked so this institution cannot be restarted on the wrong plan.
              </div>
            ) : (
              <div className="institution-plan-grid" style={{ display: "grid", gridTemplateColumns: displayPlans.length === 1 ? "minmax(222px,420px)" : "repeat(auto-fit,minmax(222px,1fr))", justifyContent: displayPlans.length === 1 ? "center" : "stretch", gap: 14 }}>
                {displayPlans.map((plan) => {
                  const sourcePlan = plan.sourcePlan || plan;
                  const active = plan.isBusinessDemoCard
                    ? isBusinessInstitutionPlan(selectedPlan)
                    : selectedPlan.tier === plan.tier;
                  return (
                    <PlanCard
                      key={plan.tier}
                      C={C}
                      plan={plan}
                      active={active}
                      cycle={cycle}
                      priceLabel={getPlanPriceLabel(sourcePlan)}
                      institutionBusinessDemoMode={institutionBusinessDemoMode}
                      onSelect={() => choosePlan(sourcePlan)}
                    />
                  );
                })}
              </div>
            )}
          </div>

          <aside className="institution-checkout-card" style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 22, boxShadow: C.shadow, overflow: "hidden", position: "sticky", top: 88 }}>
            <div style={{ padding: 20, borderBottom: `1px solid ${C.border}`, background: "linear-gradient(135deg,#EFF7FF 0%,#FFFFFF 52%,#EDF4FC 100%)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
                <div>
                  <div style={{ color: C.faint, fontSize: 11, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.5 }}>Selected Plan</div>
                  <h2 style={{ margin: "3px 0 0", fontSize: 24, fontWeight: 950 }}>{institutionPlanDisplayName(selectedPlan)}</h2>
                </div>
                <div style={{ textAlign: "right", fontWeight: 950, fontSize: 20 }}>
                  {selectedTotal}
                  <span style={{ color: C.faint, fontSize: 12, fontWeight: 800 }}>{selectedTotalSuffix}</span>
                  <div style={{ color: selectedPlanTone, fontSize: 11, fontWeight: 900 }}>{selectedPlan.size}</div>
                </div>
              </div>
            </div>

            {created ? (
              <div style={{ padding: 18 }}>
                <div style={{ display: "inline-flex", width: 46, height: 46, borderRadius: 14, alignItems: "center", justifyContent: "center", background: "rgba(52,211,153,0.12)", border: "1px solid rgba(52,211,153,0.28)", color: C.accent2, marginBottom: 12 }}>
                  <FontAwesomeIcon icon={faCheck} />
                </div>
                <h2 style={{ margin: "0 0 8px", fontSize: 22 }}>{type === "Challenge" ? "Challenge created" : "Institution created"}</h2>
                <p style={{ margin: "0 0 16px", color: C.muted, lineHeight: 1.55 }}>Share this code with members so they can join from the ProAm app.</p>
                <button type="button" onClick={copyCode} style={{ width: "100%", minHeight: 58, borderRadius: 14, border: `1px solid ${C.borderStrong}`, background: C.elevated, color: C.text, fontSize: 22, fontWeight: 950, letterSpacing: 2, cursor: "pointer", marginBottom: 12 }}>
                  {created.join_code || created.institute_code}
                  <FontAwesomeIcon icon={faCopy} style={{ fontSize: 14, marginLeft: 10, color: C.accent2 }} />
                </button>
                {type !== "Challenge" && subgroupsIncluded && (
                  <div style={{ border: `1px solid ${C.border}`, background: C.elevated, borderRadius: 14, padding: 12, marginBottom: 12 }}>
                    <div style={{ color: C.text, fontSize: 13, fontWeight: 950, marginBottom: 4 }}>
                      {structureCopy} included
                    </div>
                    <p style={{ margin: 0, color: C.muted, fontSize: 12, lineHeight: 1.45, fontWeight: 750 }}>
                      Your members leaderboard is ready. Open the dashboard next to create {subgroupLabel}, invite people into them, or create a time-limited challenge.
                    </p>
                  </div>
                )}
                <div style={{ display: "grid", gap: 9 }}>
                  <ActionButton C={C} onClick={() => navigate("/profile?tab=institutes")}>
                    {type === "Challenge" ? "Open Challenges" : subgroupsIncluded ? `Create ${subgroupLabel}` : "Open Dashboard"} <FontAwesomeIcon icon={faArrowRight} />
                  </ActionButton>
                  <ActionButton C={C} secondary onClick={() => setCreated(null)}>
                    Create Another
                  </ActionButton>
                </div>
              </div>
            ) : (
              <form onSubmit={startTrial} style={{ padding: 18, display: "grid", gap: 14 }}>
                {!user && !contactDetailsRequired && (
                  <div style={{ border: `1px solid rgba(245,158,11,0.35)`, background: "rgba(245,158,11,0.09)", borderRadius: 12, padding: 12, color: C.warn, fontSize: 13, fontWeight: 850, lineHeight: 1.45 }}>
                    <FontAwesomeIcon icon={faLock} style={{ marginRight: 8 }} />
                    Sign in before starting institution access.
                  </div>
                )}
                {existingCheckoutMode ? (
                  <div style={{ display: "grid", gap: 10, border: `1px solid ${C.border}`, background: C.elevated, borderRadius: 12, padding: 12 }}>
                    <div>
                      <div style={{ color: C.faint, fontSize: 11, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.5 }}>
                        {restartExistingAccess ? "Existing institution" : "Existing workspace"}
                      </div>
                      <div style={{ color: C.text, fontSize: 18, fontWeight: 950, marginTop: 3 }}>
                        {existingInstituteStatus === "loading" ? "Loading institution..." : existingInstitute?.name || workspaceName || "Institution"}
                      </div>
                    </div>
                    <div style={{ color: C.muted, fontSize: 12, lineHeight: 1.5, fontWeight: 800 }}>
                      {restartExistingAccess
                        ? "Membership will restart for this institution. Members, join code, departments, branding and step history stay the same."
                        : "Billing will update this existing institution. Members, join code, departments and branding stay the same."}
                    </div>
                    <div style={{ display: "grid", gap: 7 }}>
                      {[
                        ["Type", existingInstitute?.institute_type || type || "Institution"],
                        ["Country", existingInstitute?.country || country || "Not set"],
                        ["Region", existingInstitute?.state_province || region || "Not set"],
                        ["Plan", lockedExistingPlan ? institutionPlanDisplayName(lockedExistingPlan) : institutionPlanDisplayName(selectedPlan)],
                      ].map(([label, value]) => (
                        <div key={label} style={{ display: "flex", justifyContent: "space-between", gap: 10, color: C.muted, fontSize: 12, fontWeight: 850 }}>
                          <span>{label}</span>
                          <strong style={{ color: C.text, textAlign: "right" }}>{value}</strong>
                        </div>
                      ))}
                    </div>
                    {(existingInstituteError || restartPlanBlocked) && (
                      <div role="alert" style={{ border: `1px solid rgba(239,68,68,0.34)`, background: "rgba(239,68,68,0.08)", borderRadius: 10, padding: 10, color: C.danger, fontSize: 12, fontWeight: 850, lineHeight: 1.4 }}>
                        {restartPlanBlocked ? "Previous membership plan could not be confirmed." : existingInstituteError}
                      </div>
                    )}
                  </div>
                ) : businessDemoOnly ? (
                  <div style={{ display: "grid", gap: 8, border: `1px solid ${C.border}`, background: C.elevated, borderRadius: 12, padding: 12 }}>
                    <div style={{ color: C.text, fontSize: 15, fontWeight: 950 }}>Book a Demo</div>
                    <div style={{ color: C.muted, fontSize: 13, lineHeight: 1.5, fontWeight: 750 }}>
                      No institute profile setup is needed yet. Choose a time and we will walk through the right business setup.
                    </div>
                  </div>
                ) : (
                  <>
                <label style={labelStyle(C)}>
                  {type === "Challenge" ? "Organiser / workspace name" : "Account / workspace name"}
                  <input value={workspaceName} onChange={(e) => setWorkspaceName(e.target.value)} onKeyDown={handleWorkspaceNameKeyDown} style={field(C, errors.workspaceName)} placeholder={type === "Challenge" ? "Challenge organiser" : "Workspace name"} autoComplete="organization" enterKeyHint="next" />
                  {errors.workspaceName && <span style={{ color: C.danger, textTransform: "none", letterSpacing: 0 }}>{errors.workspaceName}</span>}
                </label>

                {type === "Challenge" ? (
                  <label style={labelStyle(C)}>
                    Challenge name
                    <input ref={challengeNameInputRef} value={name} onChange={(e) => setName(e.target.value)} style={field(C, errors.name)} placeholder="e.g. May Steps Challenge" autoComplete="organization-unit" />
                    {errors.name && <span style={{ color: C.danger, textTransform: "none", letterSpacing: 0 }}>{errors.name}</span>}
                  </label>
                ) : (
                  <div style={{ border: `1px solid ${C.border}`, background: C.elevated, borderRadius: 12, padding: 12, color: C.muted, fontSize: 12, lineHeight: 1.5, fontWeight: 750 }}>
                    {generatedDashboardCopy}
                  </div>
                )}

                <label style={labelStyle(C)}>
                  Institution type
                  <span style={{ position: "relative" }}>
                    <select ref={institutionTypeSelectRef} value={type} onChange={(e) => setType(e.target.value)} style={{ ...field(C, errors.type), appearance: "none" }}>
                      {INSTITUTE_TYPES.map((item) => <option key={item} value={item}>{item}</option>)}
                    </select>
                    <FontAwesomeIcon icon={faChevronDown} style={{ position: "absolute", right: 13, top: 15, color: C.faint, pointerEvents: "none" }} />
                  </span>
                </label>

                {type === "Challenge" && (
                  <div style={{ display: "grid", gap: 10, border: `1px solid ${C.border}`, background: C.elevated, borderRadius: 12, padding: 12 }}>
                    <div>
                      <div style={{ color: C.text, fontSize: 13, fontWeight: 950 }}>Challenge rules</div>
                      <div style={{ color: C.faint, fontSize: 12, fontWeight: 800, marginTop: 3, lineHeight: 1.45 }}>Start date, finish date, and participant cap are required before members can join.</div>
                    </div>
                    <div className="institution-form-columns" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                      <label style={labelStyle(C)}>
                        Start date
                        <input type="date" min={toDateInputValue(new Date())} value={challengeStartsOn} onChange={(e) => setChallengeStartsOn(e.target.value)} style={field(C, errors.challengeStartsOn)} />
                        {errors.challengeStartsOn && <span style={{ color: C.danger, textTransform: "none", letterSpacing: 0 }}>{errors.challengeStartsOn}</span>}
                      </label>
                      <label style={labelStyle(C)}>
                        Finish date
                        <input type="date" min={challengeStartsOn || toDateInputValue(new Date())} value={challengeEndsOn} onChange={(e) => setChallengeEndsOn(e.target.value)} style={field(C, errors.challengeEndsOn)} />
                        {errors.challengeEndsOn && <span style={{ color: C.danger, textTransform: "none", letterSpacing: 0 }}>{errors.challengeEndsOn}</span>}
                      </label>
                    </div>
                    <label style={labelStyle(C)}>
                      Allowed participants
                      <input type="number" min="1" value={challengeMemberLimit} onChange={(e) => setChallengeMemberLimit(e.target.value)} style={field(C, errors.challengeMemberLimit)} inputMode="numeric" placeholder="e.g. 80" />
                      {errors.challengeMemberLimit && <span style={{ color: C.danger, textTransform: "none", letterSpacing: 0 }}>{errors.challengeMemberLimit}</span>}
                    </label>
                  </div>
                )}

                <div className="institution-form-columns" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <label style={labelStyle(C)}>
                    Country
                    <span style={{ position: "relative" }}>
                      <select value={countryCode} onChange={(e) => chooseCountry(e.target.value)} style={{ ...field(C, errors.country), appearance: "none" }} autoComplete="country-name">
                        {COUNTRIES.map((item) => <option key={item.isoCode} value={item.isoCode}>{item.name}</option>)}
                      </select>
                      <FontAwesomeIcon icon={faChevronDown} style={{ position: "absolute", right: 13, top: 15, color: C.faint, pointerEvents: "none" }} />
                    </span>
                    {errors.country && <span style={{ color: C.danger, textTransform: "none", letterSpacing: 0 }}>{errors.country}</span>}
                  </label>
                  <label style={labelStyle(C)}>
                    Region
                    {regionOptions.length > 0 ? (
                      <span style={{ position: "relative" }}>
                        <select value={region} onChange={(e) => setRegion(e.target.value)} style={{ ...field(C, errors.region), appearance: "none" }} autoComplete="address-level1">
                          <option value="">Select region</option>
                          {regionOptions.map((item) => <option key={item.isoCode} value={item.name}>{item.name}</option>)}
                        </select>
                        <FontAwesomeIcon icon={faChevronDown} style={{ position: "absolute", right: 13, top: 15, color: C.faint, pointerEvents: "none" }} />
                      </span>
                    ) : (
                      <input value={region} onChange={(e) => setRegion(e.target.value)} style={field(C, errors.region)} placeholder="Region / state" autoComplete="address-level1" />
                    )}
                    {errors.region && <span style={{ color: C.danger, textTransform: "none", letterSpacing: 0 }}>{errors.region}</span>}
                  </label>
                </div>

                <label style={labelStyle(C)}>
                  {type === "Company" ? "Square company logo URL" : "Image URL"}
                  <input value={logoUrl} onChange={(e) => setLogoUrl(e.target.value)} style={field(C)} placeholder={type === "Company" ? "Square logo image URL" : "Optional image URL"} inputMode="url" />
                  <span style={{ color: C.faint, fontSize: 11, fontWeight: 800, lineHeight: 1.45, textTransform: "none", letterSpacing: 0 }}>
                    {type === "Company"
                      ? "Use a standalone square logo. Company leaderboard cards are not designed for wide landscape photos."
                      : "Use an image that represents the institution, team, club, or challenge."}
                  </span>
                </label>

                <div className="institution-form-columns" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <label style={labelStyle(C)}>
                    Primary
                    <input value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} style={field(C)} />
                  </label>
                  <label style={labelStyle(C)}>
                    Accent
                    <input value={accentColor} onChange={(e) => setAccentColor(e.target.value)} style={field(C)} />
                  </label>
                </div>
                  </>
                )}

                <div style={{ display: "grid", gap: 8, background: C.elevated, border: `1px solid ${C.border}`, borderRadius: 12, padding: 12 }}>
                  {billingPricesStatus !== "ready" && (
                    <div role={billingPricesStatus === "loading" ? "status" : "alert"} style={{ border: `1px solid ${billingPricesStatus === "loading" ? C.borderStrong : "rgba(245,158,11,0.35)"}`, background: billingPricesStatus === "loading" ? "rgba(255,255,255,0.03)" : "rgba(245,158,11,0.09)", borderRadius: 10, padding: 10, color: billingPricesStatus === "loading" ? C.faint : C.warn, fontSize: 12, fontWeight: 850, lineHeight: 1.4 }}>
                      {billingPricesStatus === "loading" ? "Loading live billing prices..." : "Live billing prices could not load. Showing saved website prices for now."}
                    </div>
                  )}
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", color: C.muted, fontSize: 13, fontWeight: 850 }}>
                    <span>Base plan</span>
                    <strong style={{ color: C.text }}>{selectedBaseLabel}</strong>
                  </div>
                  {usageBased && !businessDemoRequired && (
                    <>
                      <label style={{ ...labelStyle(C), textTransform: "none", letterSpacing: 0, color: C.muted, borderTop: `1px solid ${C.border}`, paddingTop: 8 }}>
                        Active member count
                        <input
                          type="number"
                          min={includedMembers || 1}
                          value={activeMemberCount}
                          onChange={(event) => setActiveMemberCount(event.target.value)}
                          style={{
                            ...field(C),
                            minHeight: 50,
                            border: `2px solid ${C.accent}`,
                            background: "#FFFFFF",
                            color: "#0A1D44",
                            boxShadow: "0 0 0 4px rgba(12,105,200,0.12)",
                            fontSize: 16,
                            fontWeight: 950,
                          }}
                          inputMode="numeric"
                        />
                      </label>
                      <div style={{ color: C.faint, fontSize: 12, fontWeight: 800, lineHeight: 1.45 }}>
                        Your base plan includes {includedMembers.toLocaleString()} active members. Use a lower self-serve plan for smaller groups; counts above the included base use a rollout quote.
                      </div>
                      {usageBelowIncluded && (
                        <div style={{ border: `1px solid rgba(245,158,11,0.35)`, background: "rgba(245,158,11,0.09)", borderRadius: 12, padding: 12, color: C.warn, fontSize: 13, fontWeight: 850, lineHeight: 1.45 }}>
                          <strong style={{ display: "block", color: C.text, marginBottom: 4 }}>Choose a smaller plan</strong>
                          {institutionPlanDisplayName(selectedPlan)} starts at {includedMembers.toLocaleString()} active members. For {effectiveMemberCount.toLocaleString()} active members, select a lower self-serve plan instead.
                        </div>
                      )}
                      {overageBreakdown.length > 0 && (
                        <div style={{ display: "grid", gap: 8, border: `1px solid ${C.border}`, borderRadius: 10, padding: 10, background: "rgba(255,255,255,0.03)" }}>
                          <div>
                            <div style={{ color: C.text, fontSize: 12, fontWeight: 950 }}>Extra member estimate</div>
                            <div style={{ color: C.faint, fontSize: 11, fontWeight: 800, marginTop: 2 }}>Only members above {includedMembers.toLocaleString()} are charged here.</div>
                          </div>
                          {overageBreakdown.map((line) => (
                            <div key={`${line.from}-${line.to || "plus"}`} style={{ display: "flex", justifyContent: "space-between", gap: 10, color: C.muted, fontSize: 12, fontWeight: 800 }}>
                              <span>{line.count.toLocaleString()} members at {formatInstitutionPrice(line.amount_cents)} each</span>
                              <strong style={{ color: C.text }}>{formatInstitutionPrice(line.line_total_cents)}</strong>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", color: C.text, fontSize: 14, fontWeight: 950, borderTop: `1px solid ${C.border}`, paddingTop: 8 }}>
                    <span>{usageBased ? cycle === "yearly" ? "Estimated yearly" : "Estimated monthly" : "Total"}</span>
                    <span>{selectedTotal}{selectedTotalSuffix}</span>
                  </div>
                  {!customClubPricingRequired && !businessDemoRequired && (
                    <>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", color: C.muted, fontSize: 13, fontWeight: 850 }}>
                        <span>{cycle === "yearly" ? "Per member/year" : "Per member/month"}</span>
                        <strong style={{ color: C.text }}>{formatInstitutionEffectiveRate(cycle === "yearly" ? perMemberYearlyTotalCents : perMemberMonthlyTotalCents, perMemberDivisor)}</strong>
                      </div>
                    </>
                  )}
                  <div style={{ display: "flex", gap: 9, alignItems: "center", color: C.muted, fontSize: 13, fontWeight: 850 }}>
                    <FontAwesomeIcon icon={faUsers} style={{ color: C.accent }} />
                    {type === "Challenge" ? "Challenge access uses the selected plan size" : `Plan size: ${selectedPlan.size}`}
                  </div>
                  <div style={{ display: "flex", gap: 9, alignItems: "center", color: C.muted, fontSize: 13, fontWeight: 850 }}>
                    <FontAwesomeIcon icon={subgroupsIncluded ? faLayerGroup : faBuilding} style={{ color: subgroupsIncluded ? C.accent2 : C.faint }} />
                    {type === "Challenge" ? "Member challenge leaderboard" : subgroupsIncluded ? `${structureCopy} included` : "No team institutes on this plan"}
                  </div>
                  <div style={{ display: "flex", gap: 9, alignItems: "center", color: C.muted, fontSize: 13, fontWeight: 850 }}>
                    <FontAwesomeIcon icon={faCrown} style={{ color: C.warn }} />
                    {restartExistingAccess
                      ? "Membership restarts for this existing institution"
                      : liveDashboardPaywall
                        ? "Live branded dashboard is included with this plan"
                        : "Live branded dashboard is an upsell after setup"}
                  </div>
                </div>

                {(errors.auth || errors.submit) && (
                  <div role="alert" style={{ border: `1px solid rgba(239,68,68,0.34)`, background: "rgba(239,68,68,0.08)", borderRadius: 12, padding: 12, color: C.danger, fontSize: 13, fontWeight: 850 }}>
                    {errors.auth || errors.submit}
                  </div>
                )}
                {demoSubmitted && (
                  <div role="status" style={{ border: `1px solid rgba(52,211,153,0.34)`, background: "rgba(52,211,153,0.09)", borderRadius: 12, padding: 12, color: C.accent2, fontSize: 13, fontWeight: 850, lineHeight: 1.45 }}>
                    {demoSubmitted}
                  </div>
                )}

                {calendlyDemoRequired && (
                  <div style={{ display: "grid", gap: 10, border: `1px solid ${C.border}`, background: C.elevated, borderRadius: 12, padding: 12 }}>
                    <div style={{ color: C.text, fontSize: 14, fontWeight: 950 }}>Schedule your demo in Calendly</div>
                    <div style={{ color: C.muted, fontSize: 12, lineHeight: 1.5, fontWeight: 750 }}>
                      Pick a time for the Business institution rollout call. No institute profile setup is needed yet.
                    </div>
                  </div>
                )}

                {contactDetailsRequired && (
                  <div style={{ display: "grid", gap: 10, border: `1px solid ${C.border}`, background: C.elevated, borderRadius: 12, padding: 12 }}>
                    <label style={labelStyle(C)}>
                      Contact name
                      <input value={contactName} onChange={(e) => setContactName(e.target.value)} style={field(C, errors.contactName)} placeholder="Your name" autoComplete="name" />
                      {errors.contactName && <span style={{ color: C.danger, textTransform: "none", letterSpacing: 0 }}>{errors.contactName}</span>}
                    </label>
                    <label style={labelStyle(C)}>
                      Contact email
                      <input type="email" value={contactEmailAddress} onChange={(e) => setContactEmailAddress(e.target.value)} style={field(C, errors.contactEmailAddress)} placeholder="you@example.com" autoComplete="email" />
                      {errors.contactEmailAddress && <span style={{ color: C.danger, textTransform: "none", letterSpacing: 0 }}>{errors.contactEmailAddress}</span>}
                    </label>
                    <label style={labelStyle(C)}>
                      Phone number
                      <input type="tel" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} style={field(C)} placeholder="+353..." autoComplete="tel" />
                    </label>
                    <div style={{ color: C.faint, fontSize: 12, fontWeight: 800, lineHeight: 1.45 }}>
                      This sends a demo request to {CONTACT_EMAIL} and stores it for follow-up.
                    </div>
                  </div>
                )}

                <ActionButton C={C} type="submit" disabled={submitting || restartPlanBlocked || (existingCheckoutMode && existingInstituteStatus === "loading") || (!user && !contactDetailsRequired && !calendlyDemoRequired) || usageBelowIncluded}>
                  {usageBelowIncluded
                    ? "Choose a smaller plan"
                    : calendlyDemoRequired
                      ? "Book a Demo"
                      : contactDetailsRequired
                      ? submitting ? "Sending request..." : "Book a Demo"
                      : submitting
                        ? existingCheckoutMode ? "Opening checkout..." : "Creating..."
                        : restartExistingAccess
                          ? "Restart membership"
                          : existingCheckoutMode
                            ? "Continue to checkout"
                            : type === "Challenge"
                              ? "Create Challenge"
                              : "Start Access"} <FontAwesomeIcon icon={faArrowRight} />
                </ActionButton>
              </form>
            )}
          </aside>
        </div>
      </section>

      <style>{`
        .institution-create-page * { box-sizing: border-box; }
        .institution-create-grid > * { min-width: 0; }
        .institution-create-header { box-shadow: 0 1px 0 rgba(10,29,68,0.02); }
        .institution-create-header img { display: block; width: auto; }
        .institution-create-header button { min-height: 44px; padding: 0 10px; border-radius: 10px !important; }
        .institution-create-header button:hover { color: #0C69C8 !important; background: #EEF6FF !important; }
        .institution-create-hero { position: relative; isolation: isolate; overflow: hidden; padding: 30px; border: 1px solid rgba(255,255,255,0.16); border-radius: 24px; box-shadow: 0 22px 54px rgba(10,29,68,0.18); }
        .institution-create-hero-orb { position: absolute; border-radius: 999px; pointer-events: none; }
        .institution-create-hero-orb.one { width: 360px; height: 360px; right: -120px; top: -210px; border: 1px solid rgba(255,255,255,0.17); box-shadow: 0 0 0 46px rgba(255,255,255,0.045),0 0 0 92px rgba(255,255,255,0.025); }
        .institution-create-hero-orb.two { width: 220px; height: 220px; right: 18%; bottom: -190px; background: rgba(88,190,255,0.16); filter: blur(2px); }
        .institution-create-steps { display: flex; flex-wrap: wrap; gap: 10px 18px; margin: 22px 0 0; padding: 16px 0 0; border-top: 1px solid rgba(255,255,255,0.16); list-style: none; }
        .institution-create-steps li { display: inline-flex; align-items: center; gap: 8px; color: rgba(255,255,255,0.84); font-size: 12px; }
        .institution-create-steps li + li::before { content: ""; width: 20px; height: 1px; margin-right: 8px; background: rgba(255,255,255,0.26); }
        .institution-create-steps span { display: inline-grid; width: 24px; height: 24px; place-items: center; border: 1px solid rgba(255,255,255,0.34); border-radius: 999px; color: #fff; background: rgba(255,255,255,0.10); font-weight: 950; }
        .institution-create-steps strong { font-weight: 850; }
        .institution-plan-toolbar > div { box-shadow: 0 8px 24px rgba(25,52,92,0.05); }
        .institution-plan-toolbar button { min-height: 42px; transition: color .16s ease,background .16s ease,box-shadow .16s ease; }
        .institution-plan-toolbar button:hover { color: #0C69C8 !important; background: #EDF6FF !important; }
        .institution-plan-toolbar button[style*="linear-gradient"]:hover { color: #fff !important; }
        .institution-plan-card { transition: transform .18s ease,border-color .18s ease,box-shadow .18s ease; }
        .institution-plan-card:hover { transform: translateY(-3px); border-color: #8DBDEB !important; box-shadow: 0 18px 40px rgba(25,52,92,0.12) !important; }
        .institution-plan-card.active:hover { border-color: #0C69C8 !important; }
        .institution-action-button { transition: transform .16s ease,box-shadow .16s ease,background .16s ease; }
        .institution-action-button.is-primary:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 12px 28px rgba(12,105,200,0.24); }
        .institution-action-button.is-secondary:hover:not(:disabled) { border-color: #93B9DE !important; background: #EEF6FF !important; }
        .institution-create-page button:focus-visible,
        .institution-create-page a:focus-visible,
        .institution-create-page input:focus-visible,
        .institution-create-page select:focus-visible { outline: 3px solid rgba(12,105,200,0.24) !important; outline-offset: 2px; }
        .institution-create-page input:focus,
        .institution-create-page select:focus { border-color: #0C69C8 !important; box-shadow: 0 0 0 3px rgba(12,105,200,0.10); }
        .institution-create-page ~ button[data-global-drawer-toggle][aria-label="Open navigation"] { top: 13px !important; right: 92px !important; }
        @media (max-width: 980px) {
          .institution-create-grid { grid-template-columns: 1fr !important; }
          .institution-create-grid aside { position: static !important; }
          .institution-checkout-card { max-width: 720px; width: 100%; margin: 0 auto; }
        }
        @media (max-width: 700px) {
          .institution-create-header { min-height: 60px !important; padding: 0 14px !important; }
          .institution-create-header img { height: 26px !important; }
          .institution-create-content { padding: 18px 14px 56px !important; }
          .institution-create-hero { padding: 24px 20px; border-radius: 20px; }
          .institution-create-hero h1 { font-size: clamp(30px,10vw,42px) !important; letter-spacing: -1px !important; }
          .institution-create-hero p { font-size: 14px !important; }
          .institution-create-steps { display: grid; gap: 9px; }
          .institution-create-steps li + li::before { display: none; }
          .institution-plan-toolbar { display: grid !important; grid-template-columns: 1fr; }
          .institution-plan-toolbar > div { display: grid !important; grid-template-columns: repeat(2,minmax(0,1fr)); width: 100%; }
          .institution-plan-toolbar > div:first-child:has(button:nth-child(4)) { grid-template-columns: repeat(4,minmax(0,1fr)); overflow-x: auto; }
          .institution-plan-toolbar button { padding: 9px 8px !important; white-space: nowrap; }
          .institution-plan-grid { display: grid !important; width: 100%; max-width: 100%; grid-auto-flow: column; grid-auto-columns: minmax(270px,84vw); grid-template-columns: none !important; justify-content: start !important; gap: 12px !important; overflow-x: auto; overscroll-behavior-x: contain; scroll-snap-type: x mandatory; padding: 3px 14px 18px 3px; scrollbar-width: thin; }
          .institution-plan-card { min-height: 100% !important; scroll-snap-align: start; }
          .institution-checkout-card { border-radius: 18px !important; }
        }
        @media (max-width: 540px) {
          .institution-create-page input,
          .institution-create-page select { font-size: 16px !important; }
          .institution-form-columns { grid-template-columns: 1fr !important; }
        }
        @media (prefers-reduced-motion: reduce) {
          .institution-plan-card,
          .institution-action-button { transition: none; }
          .institution-plan-card:hover,
          .institution-action-button.is-primary:hover:not(:disabled) { transform: none; }
        }
      `}</style>
    </main>
  );
}
